import React, { useRef, useEffect, useState } from "react";
import { motion, useInView } from "framer-motion";

const ease = [0.16, 1, 0.3, 1];

// On-scroll fade + rise
export const Reveal = ({ children, delay = 0, y = 40, className = "", ...rest }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, ease, delay }}
    {...rest}
  >
    {children}
  </motion.div>
);

// Masked line-by-line reveal (hero signature moment)
export const MaskedLines = ({ lines, className = "", lineClassName = "", stagger = 0.12, delay = 0.15 }) => (
  <span className={className} aria-label={lines.join(" ")}>
    {lines.map((line, i) => (
      <span key={i} className="block overflow-hidden pb-[0.08em]">
        <motion.span
          className={`block ${lineClassName}`}
          initial={{ y: "110%" }}
          animate={{ y: "0%" }}
          transition={{ duration: 1.05, ease, delay: delay + i * stagger }}
        >
          {line}
        </motion.span>
      </span>
    ))}
  </span>
);

// Single line reveal on scroll (robust opacity + rise)
export const MaskedLineOnScroll = ({ children, className = "", delay = 0 }) => (
  <motion.span
    className={`block ${className}`}
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, ease, delay }}
  >
    {children}
  </motion.span>
);

// Animated count-up. Parses a numeric prefix and keeps any suffix (%, +).
export const CountUp = ({ value, className = "", duration = 1.8 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const match = String(value).match(/^(\d+)(.*)$/);
  const target = match ? parseInt(match[1], 10) : 0;
  const suffix = match ? match[2] : "";
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(eased * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, target, duration]);

  return (
    <span ref={ref} className={className}>
      {display}
      {suffix}
    </span>
  );
};

export const Marquee = ({ text }) => (
  <div className="relative overflow-hidden border-y border-white/10 py-5 bg-[#0d0d0d]">
    <div className="marquee-track">
      <span className="font-accent text-2xl md:text-4xl tracking-wide text-zinc-500 uppercase">{text}</span>
      <span className="font-accent text-2xl md:text-4xl tracking-wide text-zinc-500 uppercase" aria-hidden="true">{text}</span>
    </div>
  </div>
);
